import { useState, useEffect } from 'react';
import { ChevronDown, Smartphone, BarChart3, ShoppingCart, MessageCircle, Zap, Monitor, Star, CheckCircle, Menu, X } from 'lucide-react';
import { logo, apple, google, img1, iosQr } from '../../ui/media';
import { useGetFAQQuery } from "../../../hooks/useCommon"
import ContactUsForm from '../common/ContactForm';

const IOS_APP_URL = "https://apps.apple.com/us/app/pluggedin-deals/id6757189674";
const ANDROID_APP_URL = "https://play.google.com/store/apps/details?id=com.pluggedin";

const PluggedInLanding = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [questions, setQuestions] = useState([])
  const { data: faqData, isSuccess: isFaqSuccess } = useGetFAQQuery()


  useEffect(() => {
    if (isFaqSuccess) {
      const faqList = faqData?.data?.data || [];
      setQuestions(faqList?.filter((q) => q?.status === 1));
      if (faqList.length > 0) {
        setExpandedFaq(new Set([faqList[0]._id])); // default expand first item safely
      }
    }
  }, [faqData, isFaqSuccess])
  

  const mockupImages = [
    "Coupon Discovery Screen",
    "Category Browse View",
    "Savings Tracker",
    "Business Dashboard"
  ];

  const features = [
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Manage Coupons Easily",
      description: "Browse, save, and redeem coupons with just a few taps"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Real-time Analytics for Businesses",
      description: "Track performance and customer engagement instantly"
    },
    {
      icon: <ShoppingCart className="w-8 h-8" />,
      title: "Category-based Browsing",
      description: "Find deals by category for a personalized experience"
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: "Contact Us & Instant Replies",
      description: "Get support when you need it, right in the app"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Quick Business Owner Approvals",
      description: "Streamlined approval process for faster deal publishing"
    },
    {
      icon: <Monitor className="w-8 h-8" />,
      title: "Mobile-first Experience",
      description: "Optimized for seamless mobile usage everywhere"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % mockupImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-orange-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-2">
              <span className="text-xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                <img src={logo} className='h-16' />
              </span>
            </div>

            <div className="hidden md:flex items-center space-x-8">
              <a href="#aboutus" className="text-gray-700 hover:text-orange-600 transition-colors">About Us</a>
              <a href="#features" className="text-gray-700 hover:text-orange-600 transition-colors">Features</a>
              <a href="#download" className="text-gray-700 hover:text-orange-600 transition-colors">Download</a>
              <a href="#business" className="text-gray-700 hover:text-orange-600 transition-colors">Business</a>
              <a href="#faq" className="text-gray-700 hover:text-orange-600 transition-colors">FAQ</a>
              <a href="#contactus" className="text-gray-700 hover:text-orange-600 transition-colors">Contact Us</a>

              {/* <a href="#faq" className="text-gray-700 hover:text-orange-600 transition-colors">Contact Us</a> */}
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-orange-100 transition-colors">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-orange-100">
            <div className="px-4 py-3 space-y-3">
              <a href="#aboutus" className="block text-gray-700 hover:text-orange-600 transition-colors">About Us</a>
              <a href="#features" className="block text-gray-700 hover:text-orange-600 transition-colors">Features</a>
              <a href="#download" className="block text-gray-700 hover:text-orange-600 transition-colors">Download</a>
              <a href="#business" className="block text-gray-700 hover:text-orange-600 transition-colors">Business</a>
              <a href="#faq" className="block text-gray-700 hover:text-orange-600 transition-colors">FAQ</a>
              <a href="#contactus" className="block text-gray-700 hover:text-orange-600 transition-colors">Contact Us</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                <img src={logo} className='w-[130px]' />
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-3">
                Smarter Savings,
                <br />
                Better Deals
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl">
                Connect with local businesses and discover exclusive coupons tailored just for you.
                Save money while supporting your community.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                <a href={IOS_APP_URL} target="_blank" rel="noopener noreferrer" className="bg-black text-white px-4 rounded-xl hover:bg-gray-800 transition-all duration-300 flex items-center justify-center space-x-2 transform hover:scale-105">
                  <div className="text-left">
                    <img src={apple} className='h-12' />
                  </div>
                </a>
                <a href={ANDROID_APP_URL} target="_blank" rel="noopener noreferrer" className="bg-black text-white px-4 rounded-xl hover:bg-gray-800 transition-all duration-300 flex items-center justify-center space-x-3 transform hover:scale-105">
                  <div className="text-left">
                    <img src={google} className='h-12' />
                  </div>
                </a>
              </div>
              <div className="flex items-center justify-center lg:justify-start space-x-6 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-yellow-400 fill-current" />
                  <span>4.8 Rating</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>50k+ Downloads</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative mx-auto w-72 h-[auto] bg-gradient-to-br from-gray-900 to-gray-700 rounded-[3rem] p-2 shadow-2xl">
                <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                  <div className="pt-12 px-6 h-full">
                    <img src={img1} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Overview */}
      <section id="aboutus" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl p-8 h-96 relative overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center transform transition-all duration-1000">
                    <div className="w-24 h-24 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                      <Smartphone className="w-12 h-12 text-white" />
                    </div>
                    <div className="text-xl font-bold text-gray-800">
                      {mockupImages[currentSlide]}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center mt-4 space-x-2">
                {mockupImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-colors ${index === currentSlide ? 'bg-orange-500' : 'bg-gray-300'
                      }`}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  Everything you need in one app
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Discover local deals, track your savings, and never miss out on the best offers in your area.
                </p>
              </div>

              <div className="space-y-6">
                {[
                  { icon: "🎯", title: "Find deals instantly", desc: "Smart location-based recommendations" },
                  { icon: "📊", title: "Use smart categories", desc: "Browse by food, retail, services, and more" },
                  { icon: "💰", title: "Track your savings", desc: "See exactly how much you've saved over time" }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 rounded-xl hover:bg-orange-50 transition-colors">
                    <div className="text-2xl">{benefit.icon}</div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">{benefit.title}</div>
                      <div className="text-gray-600">{benefit.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Powerful features for everyone
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Whether you're a customer hunting for deals or a business owner looking to attract customers,
              we've got the tools you need.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 group"
              >
                <div className="text-orange-600 mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download App Section */}
      <section id="download" className="py-20 bg-gradient-to-r from-gray-600 to-red-600 text-white relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <span style={{ fontSize: 50, fontFamily: "monospace" }}>Download</span><br />
          <div className="mx-auto text-center">
            <img src={logo} className='mx-auto' /><br />
          </div>
          <span>Today</span><br />
          <p className="text-xl mb-10 text-white/90">
            Join thousands of users saving money with exclusive local deals
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <a href={IOS_APP_URL} target="_blank" rel="noopener noreferrer" className="bg-black/20 backdrop-blur-sm text-white px-8 py-4 rounded-xl hover:bg-black/30 transition-all duration-300 flex items-center space-x-3 transform hover:scale-105">
              <div className="text-left">
                <img src={apple} className='h-12' />
              </div>
            </a>

            <a href={ANDROID_APP_URL} target="_blank" rel="noopener noreferrer" className="bg-black/20 backdrop-blur-sm text-white px-8 py-4 rounded-xl hover:bg-black/30 transition-all duration-300 flex items-center space-x-3 transform hover:scale-105">
              <div className="text-left">
                <img src={google} className='h-12' />
              </div>
            </a>

            <div className="bg-white p-4 rounded-xl">
              <a href={IOS_APP_URL} target="_blank" rel="noopener noreferrer">
                <img src={iosQr} alt="QR code to download PluggedIn on the App Store" className="w-24 h-24 mx-auto" />
              </a>
              <div className="text-gray-600 text-sm mt-2">Scan to download</div>
            </div>
          </div>
        </div>
      </section>

      {/* Business App Section */}
      <section id="business" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Two Apps, One Powerful Platform
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              PluggedIn offers specialized experiences for both business owners and customers,
              each designed to maximize value and streamline the coupon ecosystem.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Customer App */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4 w-20 h-20 bg-blue-200 rounded-full opacity-30"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mb-6">
                  <ShoppingCart className="w-8 h-8 text-white" />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4">For Customers</h3>
                <p className="text-gray-600 mb-6">
                  Discover amazing deals, browse by categories, and track your savings all in one place.
                </p>

                <ul className="space-y-3 mb-8">
                  {[
                    "Browse coupons by category and location",
                    "Get personalized deal recommendations",
                    "Track total savings and usage history",
                    "Rate and review business experiences",
                    "Get notifications for new deals nearby"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-white rounded-xl p-4 shadow-md">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">Your Savings</span>
                    <span className="text-xs text-gray-500">This Month</span>
                  </div>
                  <div className="text-2xl font-bold text-green-600">$247.50</div>
                  <div className="text-xs text-gray-500">15 coupons used</div>
                </div>
              </div>
            </div>

            {/* Business App */}
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-3xl p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4 w-20 h-20 bg-orange-200 rounded-full opacity-30"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mb-6">
                  <BarChart3 className="w-8 h-8 text-white" />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4">For Businesses</h3>
                <p className="text-gray-600 mb-6">
                  Create targeted discount campaigns and track performance with detailed analytics.
                </p>

                <ul className="space-y-3 mb-8">
                  {[
                    "Create and manage discount coupons easily",
                    "Set expiration dates and usage limits",
                    "Track coupon performance in real-time",
                    "View customer engagement analytics",
                    "Monitor top-rated vs. underperforming offers"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-white rounded-xl p-4 shadow-md">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <div className="text-lg font-bold text-green-600">245</div>
                      <div className="text-xs text-gray-500">Active</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-orange-600">18</div>
                      <div className="text-xs text-gray-500">Inactive</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-red-600">12</div>
                      <div className="text-xs text-gray-500">Expired</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Analytics Dashboard Preview */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-700 rounded-3xl p-8 relative overflow-hidden">
            <div className="bg-white rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-xl font-bold text-gray-900">Business Analytics Dashboard</h4>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Live Data</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl">
                  <div className="text-blue-600 text-2xl font-bold mb-1">2,547</div>
                  <div className="text-blue-700 text-sm font-medium mb-1">Total Coupons Used</div>
                  <div className="text-blue-600 text-xs">↗ +12% this week</div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl">
                  <div className="text-green-600 text-2xl font-bold mb-1">4.8★</div>
                  <div className="text-green-700 text-sm font-medium mb-1">Top Rated Coupons</div>
                  <div className="text-green-600 text-xs">Customer satisfaction</div>
                </div>

                <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-xl">
                  <div className="text-orange-600 text-2xl font-bold mb-1">187</div>
                  <div className="text-orange-700 text-sm font-medium mb-1">Active Campaigns</div>
                  <div className="text-orange-600 text-xs">15 expiring soon</div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-xl">
                  <div className="text-purple-600 text-2xl font-bold mb-1">$24.5K</div>
                  <div className="text-purple-700 text-sm font-medium mb-1">Revenue Impact</div>
                  <div className="text-purple-600 text-xs">Through coupons</div>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-xl">
                  <h5 className="font-semibold text-gray-900 mb-3">Coupon Performance</h5>
                  <div className="h-32 bg-gradient-to-r from-orange-200 to-red-200 rounded-lg flex items-center justify-center">
                    <span className="text-gray-600">Performance Chart</span>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-xl">
                  <h5 className="font-semibold text-gray-900 mb-3">Top Performing Coupons</h5>
                  <div className="space-y-3">
                    {[
                      { name: "50% Off Pizza", uses: 1240, rating: "4.9" },
                      { name: "Buy 1 Get 1 Coffee", uses: 987, rating: "4.8" },
                      { name: "20% Off Spa Services", uses: 756, rating: "4.7" }
                    ].map((coupon, index) => (
                      <div key={index} className="flex items-center justify-between py-2">
                        <div>
                          <div className="font-medium text-sm text-gray-900">{coupon.name}</div>
                          <div className="text-xs text-gray-500">{coupon.uses} uses</div>
                        </div>
                        <div className="text-sm font-medium text-green-600">{coupon.rating}★</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about PluggedIn
            </p>
          </div>

          <div className="space-y-4">
            {questions.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-md">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-orange-50 transition-colors">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-orange-600 transition-transform ${expandedFaq === index ? 'rotate-180' : ''
                      }`}
                  />
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contactus" className="py-16 bg-gradient-to-br from-orange-50 to-red-50">
        <ContactUsForm/>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-8 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-[150px] p-4 bg-[#fff] rounded-lg flex items-center justify-center">
                  <img src={logo} className='w-[100px]' />
                </div>

              </div>
              <p className="text-gray-300 max-w-md">
                Connecting customers with local businesses through smart coupon discovery and savings tracking.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#aboutus" className="hover:text-orange-400 transition-colors">About</a></li>
                <li><a href="#features" className="hover:text-orange-400 transition-colors">Features</a></li>
                <li><a href="#business" className="hover:text-orange-400 transition-colors">Business</a></li>
                <li><a href="#download" className="hover:text-orange-400 transition-colors">Download App</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#faq" className="hover:text-orange-400 transition-colors">FAQ's</a></li>
                <li><a href="/terms" className="hover:text-orange-400 transition-colors">Terms of Service</a></li>
                <li><a href="/privacy" className="hover:text-orange-400 transition-colors">Privacy Policy</a></li>
                <li><a href="/delete-account" className="hover:text-orange-400 transition-colors">Delete Account</a></li>
                <li><a href="#contactus" className="hover:text-orange-400 transition-colors">Contact Us</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-4 pt-4 text-center text-gray-400">
            <p>&copy; 2025 PluggedIn. All rights reserved. Build and Maintained By SolidAppMaker</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PluggedInLanding;